import React, { useMemo, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FaRedo } from "react-icons/fa";
import {
  generateRepeatingSlots,
  MAX_REPEAT_SESSIONS,
  WEEKDAYS,
} from "../utils/availabilitySessions";

const initialState = {
  activity: "",
  location: "",
  capacity: 0,
  startTime: "08:00",
  endTime: "12:00",
  fromDate: "",
  toDate: "",
  weekdays: [],
};

// Adds a run of sessions to one availability question in one go, so leaders
// never need a new question per date (forms cap out at 30 questions).
const RepeatSessionsPanel = ({ existingSlots = [], onAdd, onClose }) => {
  const [values, setValues] = useState(() => {
    // Start from the most recent slot's details — the next block of dates is
    // usually more of the same.
    const last = existingSlots[existingSlots.length - 1];
    return last
      ? {
          ...initialState,
          activity: last.activity || "",
          location: last.location || "",
          capacity: Number(last.capacity) || 0,
          startTime: last.startTime?.slice(11, 16) || initialState.startTime,
          endTime: last.endTime?.slice(11, 16) || initialState.endTime,
        }
      : initialState;
  });
  const set = (changes) => setValues((current) => ({ ...current, ...changes }));

  const toggleWeekday = (day) =>
    set({
      weekdays: values.weekdays.includes(day)
        ? values.weekdays.filter((item) => item !== day)
        : [...values.weekdays, day],
    });

  const preview = useMemo(
    () =>
      generateRepeatingSlots({
        ...values,
        existingStarts: existingSlots.map((slot) => slot.startTime),
      }),
    [values, existingSlots],
  );
  const timesInvalid = values.startTime && values.endTime && values.endTime <= values.startTime;
  const canAdd = values.activity.trim() && preview.length && !timesInvalid;

  const add = () => {
    onAdd(
      preview.map((slot) => ({
        ...slot,
        activity: values.activity.trim(),
        label: "",
        capacity: Number(values.capacity) || 0,
        location: values.location.trim(),
      })),
    );
    onClose();
  };

  return (
    <div className="repeat-sessions">
      <div className="repeat-sessions__grid">
        <Form.Group>
          <Form.Label>Activity</Form.Label>
          <Form.Control
            placeholder="Diner"
            value={values.activity}
            onChange={(event) => set({ activity: event.target.value })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Location</Form.Label>
          <Form.Control
            value={values.location}
            onChange={(event) => set({ location: event.target.value })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={values.fromDate}
            onChange={(event) =>
              set({
                fromDate: event.target.value,
                toDate: values.toDate || event.target.value,
              })
            }
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Until</Form.Label>
          <Form.Control
            type="date"
            value={values.toDate}
            min={values.fromDate || undefined}
            onChange={(event) => set({ toDate: event.target.value })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Starts (SAST)</Form.Label>
          <Form.Control
            type="time"
            value={values.startTime}
            onChange={(event) => set({ startTime: event.target.value })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Ends (SAST)</Form.Label>
          <Form.Control
            type="time"
            value={values.endTime}
            isInvalid={Boolean(timesInvalid)}
            onChange={(event) => set({ endTime: event.target.value })}
          />
          <Form.Control.Feedback type="invalid">
            End time must be after the start time.
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <Form.Label>Capacity (0 = unlimited)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={values.capacity}
            onChange={(event) => set({ capacity: event.target.value })}
          />
        </Form.Group>
      </div>

      <fieldset className="repeat-sessions__days">
        <legend>On these days</legend>
        {WEEKDAYS.map(([day, label]) => (
          <Form.Check
            key={day}
            inline
            type="checkbox"
            id={`repeat-day-${day}`}
            label={label}
            checked={values.weekdays.includes(day)}
            onChange={() => toggleWeekday(day)}
          />
        ))}
      </fieldset>

      {preview.length === MAX_REPEAT_SESSIONS && (
        <Alert variant="warning" className="mb-0">
          Only the first {MAX_REPEAT_SESSIONS} sessions will be added. Shorten the date range.
        </Alert>
      )}

      <div className="repeat-sessions__actions">
        <span>
          {preview.length
            ? `${preview.length} session${preview.length === 1 ? "" : "s"} will be added`
            : "Choose dates and at least one day"}
        </span>
        <Button type="button" variant="outline-secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={!canAdd} onClick={add}>
          <FaRedo aria-hidden="true" /> Add sessions
        </Button>
      </div>
    </div>
  );
};

export default RepeatSessionsPanel;
